import React, {useEffect, useState} from "react";
import Drawer from "@mui/material/Drawer";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import {Link, useLocation, useNavigate} from "react-router";
import {useTheme} from "@mui/material/styles";
import {Box} from "@mui/material";
import {useAuth0} from "@auth0/auth0-react";
import {useDispatch, useSelector} from "react-redux";
import {useHasPolicy} from "@config/useHasPolicy";
import {PolicyEnum} from "@config/policyEnum";
import {fetchChangePasswordUrl} from "@store/userSlice.tsx";
import {AppDispatch} from "@store/store.tsx";

interface SidebarProps {
    isOpen: boolean;
    setSidebarOpen: React.Dispatch<React.SetStateAction<boolean>>;
    menuItems: {
        path: string;
        label: string;
        icon: React.FC;
    }[];
}

const Sidebar: React.FC<SidebarProps> = ({
                                             isOpen,
                                             menuItems,
                                             setSidebarOpen,
                                         }) => {
    const location = useLocation();
    const theme = useTheme();
    const navigate = useNavigate();
    const {user} = useSelector((state: any) => state.user);
    const {logout} = useAuth0();
    const dispatch = useDispatch<AppDispatch>();
    const handleDrawerBlur = () => {
        setSidebarOpen(false);
    };
    const [policyReady, setPolicyReady] = useState(false);
    const showUserManagement = useHasPolicy(PolicyEnum.VIEW_USER_MANAGEMENT);
    const showChangePassword = useHasPolicy(PolicyEnum.VIEW_CHANGE_PASSWORD);

    const handleChangePassword = () => {
        dispatch(fetchChangePasswordUrl());
    }

    useEffect(() => {
        setPolicyReady(true);
    }, [showUserManagement, showChangePassword]);

    useEffect(() => {
        if (!policyReady) return;
        if (!showUserManagement) {
            const index = menuItems.findIndex(item => item.label === "User Management");
            if (index !== -1) {
                menuItems.splice(index, 1);
            }
        }
        if (!showChangePassword) {
            const index = menuItems.findIndex(item => item.label === "Change Password");
            if (index !== -1) {
                menuItems.splice(index, 1);
            }
        }
    }, [policyReady, showUserManagement, showChangePassword]);

    const fallbackMapping: Record<string, string> = {
        "/chat": "/",
    };

    const getActiveItemPath = () => {
        const activeItem = menuItems.find(
            (item) => location.pathname === item.path || location.pathname.startsWith(item.path + "/")
        );
        if (activeItem) {
            return activeItem.path;
        } else {
            return fallbackMapping["/chat"];
        }
    };

    const activePath = getActiveItemPath();
    useEffect(() => {
        if (activePath === "/userInfo") {
            navigate(`/userInfo/${user?.userId}`);
        }
    }, [activePath])

    const isSelected = (path: string) => {
        if (activePath === "/logout") {
            logout({
                logoutParams: {returnTo: window.location.origin},
            });
        }
        return activePath === path;
    };

    React.useEffect(() => {
        setSidebarOpen(false);
    }, [location.pathname])

    return (
        <Box>
            <Drawer
                variant="permanent"
                open={isOpen}
                onClose={handleDrawerBlur}
                sx={{
                    width: isOpen ? 240 : 70,
                    flexShrink: 0,
                    "& .MuiDrawer-paper": {
                        width: isOpen ? 240 : 70,
                        transition: "width 0.3s",
                        overflowX: "hidden",
                        paddingTop: "4.4rem",
                    },
                    "@media(max-width:634px)": {
                        display: isOpen ? "block" : "none",
                        width: 0,
                        flexShrink: 0,
                        "& .MuiDrawer-paper": {
                            width: 240,
                            transition: "width 0.3s",
                            overflowX: "hidden",
                        },
                    },
                }}
            >
                <Box>
                    <List className="!pt-0">
                        {menuItems?.map((item: any) => {
                            return (
                                <ListItem
                                    className={
                                        item.label === "My Profile" ||
                                        item.label === "Change Password" ||
                                        item.label === "Log Out"
                                            ? "md:!hidden border-b-2"
                                            : "block border-b-2"
                                    }
                                    key={item.label}
                                    disablePadding
                                    sx={{
                                        height: "60px",
                                        backgroundColor: isSelected(item.path) ? "#f5f5f5" : "transparent",
                                        borderLeft: isSelected(item.path) ? "8px solid #A60A30" : "8px solid transparent",
                                        "&:hover": {
                                          backgroundColor: "#f5f5f5",
                                        },
                                      }}
                                >
                                    <ListItemButton
                                        component={Link}
                                        onClick={() => {
                                            setSidebarOpen(false)
                                            if (item.path === "/changePassword") {
                                                handleChangePassword()
                                            }
                                        }}
                                        to={item.path === "/changePassword" ? "#" : item.path}
                                        selected={isSelected(item.path)}
                                        sx={{
                                            backgroundColor: "#fff !important", 
                                            "&.Mui-selected": {
                                              backgroundColor: "#fff !important", 
                                            },
                                            "&.Mui-selected:hover": {
                                              backgroundColor: "#f5f5f5 !important", 
                                            },
                                            "&:hover": {
                                              backgroundColor: "#f5f5f5", 
                                            },
                                            height: "60px",
                                        }}
                                    >
                                        <ListItemIcon>
                                            <item.icon
                                                style={{
                                                    backgroundColor: isSelected(item.path)
                                                        ? "#A60A30"
                                                        : "#000000",
                                                }}
                                                fill="#fff"
                                            />
                                        </ListItemIcon>
                                        {isOpen && (
                                            <ListItemText
                                                primary={item.label}
                                            />
                                        )}
                                    </ListItemButton>
                                </ListItem>
                            );
                        })}
                    </List>
                </Box>
            </Drawer>
        </Box>
    );
};
export default Sidebar;
